import { normalizeTags } from "./tagNormalization"

/** Anything a bulk tag edit can be applied to: an id and the tags it carries. */
export interface TaggableRecord {
  id: string
  tags?: string[] | null
}

interface BulkTagUpdate {
  id: string
  tags: string[]
}

interface BulkTagPlan {
  /** Records already at the tag ceiling, which the added tags did not fit on. */
  saturatedIds: string[]
  updates: BulkTagUpdate[]
}

interface ResolveBulkTagUpdatesParams {
  maxTags: number
  nextSharedTags: readonly string[]
  previousSharedTags: readonly string[]
  records: readonly TaggableRecord[]
}

/**
 * The tags every one of `records` carries.
 *
 * A single control cannot show a tag as both present and absent, so a bulk
 * editor reports only what the whole selection agrees on. A tag on some of the
 * records reads as unchecked, which makes checking it mean "add it to the rest".
 */
export function getSharedTags(records: readonly TaggableRecord[]): string[] {
  const [firstRecord, ...remainingRecords] = records
  if (!firstRecord) {
    return []
  }

  let sharedTags = normalizeTags(firstRecord.tags ?? [])

  for (const record of remainingRecords) {
    if (sharedTags.length === 0) {
      return []
    }

    const recordTags = new Set(normalizeTags(record.tags ?? []))
    sharedTags = sharedTags.filter((tag) => recordTags.has(tag))
  }

  return sharedTags
}

/**
 * The per-record writes that carry a selection from `previousSharedTags` to
 * `nextSharedTags`, alongside the records an added tag could not fit on.
 *
 * Only the difference between the two is applied, so a tag that sits on one
 * record and not the others survives an edit that never mentioned it. Records
 * the edit would leave unchanged are omitted rather than rewritten, which keeps
 * a one-tag change from writing every selected record.
 */
export function resolveBulkTagUpdates({
  maxTags,
  nextSharedTags,
  previousSharedTags,
  records,
}: ResolveBulkTagUpdatesParams): BulkTagPlan {
  const nextTags = normalizeTags(nextSharedTags, maxTags)
  const previousTags = normalizeTags(previousSharedTags, maxTags)
  const nextTagSet = new Set(nextTags)
  const previousTagSet = new Set(previousTags)

  const addedTags = nextTags.filter((tag) => !previousTagSet.has(tag))
  const removedTags = new Set(
    previousTags.filter((tag) => !nextTagSet.has(tag)),
  )

  if (addedTags.length === 0 && removedTags.size === 0) {
    return { saturatedIds: [], updates: [] }
  }

  const saturatedIds: string[] = []
  const updates: BulkTagUpdate[] = []

  for (const record of records) {
    const currentTags = normalizeTags(record.tags ?? [], maxTags)
    const keptTags = currentTags.filter((tag) => !removedTags.has(tag))
    const tags = normalizeTags([...keptTags, ...addedTags], maxTags)

    if (addedTags.some((tag) => !tags.includes(tag))) {
      saturatedIds.push(record.id)
    }

    if (!haveSameTags(currentTags, tags)) {
      updates.push({ id: record.id, tags })
    }
  }

  return { saturatedIds, updates }
}

export function haveSameTags(
  tags: readonly string[],
  otherTags: readonly string[],
): boolean {
  if (tags.length !== otherTags.length) {
    return false
  }

  const tagSet = new Set(tags)
  return otherTags.every((tag) => tagSet.has(tag))
}
